import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import BackButton from '../../common/BackButton';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import { YogaRecord, YogaMenu } from '../../../constants/Records';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  userInfo: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  tableHead: {
    backgroundColor: theme.palette.grey[200],
  },
  actionCell: {
    width: 120,
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

interface YogaRecordListProps {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: YogaRecord[];
  menus: YogaMenu[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, recordTime: string) => void;
  onResetFlags: () => void;
}

const YogaRecordList: React.FC<YogaRecordListProps> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  menus,
  executing,
  loaded,
  deleted,
  onFetch,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<YogaRecord | null>(null);

  // コンテナ側で初期化時にfetchするため、ここでは呼び出し不要

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const getMenuName = (menuId: number): string => {
    const menu = menus.find(m => m.id === menuId);
    return menu?.menu_name || `メニューID: ${menuId}`;
  };

  const getCategoryName = (menuId: number): string => {
    const menu = menus.find(m => m.id === menuId);
    return menu?.category_name || '';
  };

  const formatDateTime = (dateTimeStr: string): string => {
    try {
      const date = new Date(dateTimeStr.replace(' ', 'T'));
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateTimeStr;
    }
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/yoga/create`);
  };

  const handleEdit = (record: YogaRecord) => {
    const encodedTime = encodeURIComponent(record.record_time);
    history.push(`/institutions/${institutionId}/users/${userId}/records/yoga/${encodedTime}`);
  };

  const handleDeleteClick = (record: YogaRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord) {
      onDelete(uniqueId, selectedRecord.record_time);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  // 日時でソート（新しい順）
  const sortedRecords = [...records].sort((a, b) => {
    return new Date(b.record_time).getTime() - new Date(a.record_time).getTime();
  });

  return (
    <div className={classes.root}>
      <BackButton />

      <Box className={classes.userInfo}>
        <Typography variant="subtitle2" color="textSecondary">
          施設名: {institutionName}
        </Typography>
        <Typography variant="h6">利用者: {userName} 様</Typography>
      </Box>

      <Box className={classes.header}>
        <Typography variant="h5">ADLヨガ記録</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </Box>

      {executing && !loaded ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : sortedRecords.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>記録がありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>日時</TableCell>
                <TableCell>メニュー</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecords.map(record => (
                <TableRow key={`${record.unique_id}-${record.record_time}`} hover>
                  <TableCell>{formatDateTime(record.record_time)}</TableCell>
                  <TableCell>{getMenuName(record.menu_id)}</TableCell>
                  <TableCell>{getCategoryName(record.menu_id)}</TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton size="small" onClick={() => handleEdit(record)} title="編集">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(record)} title="削除">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="レコードの削除"
        content={
          selectedRecord && (
            <>
              <Typography>このレコードを削除しますか？</Typography>
              <Box mt={2}>
                <Typography variant="body2">日時: {formatDateTime(selectedRecord.record_time)}</Typography>
                <Typography variant="body2">メニュー: {getMenuName(selectedRecord.menu_id)}</Typography>
              </Box>
            </>
          )
        }
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default YogaRecordList;
